import { useId } from 'react';

import { formatDate } from '@betterment/core';
import { Box } from '@betterment/samba-react';
import { token } from '@betterment/samba-styled-system/tokens';
import {
  ChartProvider,
  Cursor,
  Grid,
  HiddenChartDescription,
  LineSeries,
  PlaceholderAreaChart,
  XAxis,
  XYChart,
  YAxis,
  useXYScales,
} from '@betterment/shared-components';
import { ParentSize } from '@visx/responsive';
import { isToday } from 'date-fns';
import { isNil } from 'es-toolkit/compat';

import { PerformanceHistoryTimeWeightedReturnItem } from '../../../../graphql/schema';

export type TimeWeightedReturnsChartProps = {
  timeSeries?: PerformanceHistoryTimeWeightedReturnItem[];
};

const formatDateLabel = (value: Date) => {
  return isToday(value) ? 'Today' : formatDate(value, 'longOrdinalShortMonth');
};

const yTickFormat = (value: number) => {
  const percentage = value / 100;
  return `${percentage.toFixed(0)}%`;
};

const formatReturnsDataPointLabel = (datum: PerformanceHistoryTimeWeightedReturnItem): string => {
  if (!datum) return '';

  const dateStr = formatDate(datum.date, 'longOrdinalShortMonth');
  const returnStr = isNil(datum.cumulativeReturn) ? '--' : yTickFormat(datum.cumulativeReturn);

  return `${dateStr}, Return: ${returnStr}`;
};

const TimeWeightedReturnsChartDescription: React.FC<{
  id: string;
  startDate: Date;
  endDate: Date;
  startingReturn: number;
  endingReturn: number;
  minReturn: number;
  maxReturn: number;
}> = ({ id, startDate, endDate, startingReturn, endingReturn, minReturn, maxReturn }) => {
  const change = endingReturn - startingReturn;
  let direction: string;
  if (change > 0) {
    direction = 'increased';
  } else if (change < 0) {
    direction = 'decreased';
  } else {
    direction = 'remained unchanged';
  }

  return (
    <HiddenChartDescription id={id}>
      Time-weighted returns from {formatDate(startDate)} to {formatDate(endDate)}.{'\n'}Returns {direction} from{' '}
      {yTickFormat(startingReturn)} to {yTickFormat(endingReturn)}.{'\n'}Range: {yTickFormat(minReturn)} to{' '}
      {yTickFormat(maxReturn)}.
    </HiddenChartDescription>
  );
};

export const TimeWeightedReturnsChart: React.FC<TimeWeightedReturnsChartProps> = ({ timeSeries }) => {
  const describeId = useId();

  const allValues = timeSeries?.map(d => d.cumulativeReturn ?? 0) ?? [];
  const dataMin = allValues.length > 0 ? Math.min(...allValues) : 0;
  const dataMax = allValues.length > 0 ? Math.max(...allValues) : 0;

  const validEntries = timeSeries?.filter(d => d.cumulativeReturn != null) ?? [];
  const firstEntry = validEntries[0];
  const lastEntry = validEntries[validEntries.length - 1];

  const { xScale, yScale } = useXYScales({
    yMin: dataMin,
    yMax: dataMax,
  });

  return (
    <Box css={{ height: '350px' }}>
      <ParentSize debounceTime={10}>
        {({ width }) =>
          timeSeries && timeSeries.length > 0 ? (
            <>
              <TimeWeightedReturnsChartDescription
                id={describeId}
                startDate={firstEntry.date}
                endDate={lastEntry.date}
                startingReturn={firstEntry.cumulativeReturn!}
                endingReturn={lastEntry.cumulativeReturn!}
                minReturn={dataMin}
                maxReturn={dataMax}
              />
              <ChartProvider xScale={xScale} yScale={yScale} theme={{ colors: [token('colors.dataviz.blue.2')] }}>
                <XYChart
                  width={width}
                  height={350}
                  margin={{ right: 0, top: 20, left: 50, bottom: 50 }}
                  aria-label="Time-weighted returns chart"
                  aria-describedby={describeId}
                >
                  <Grid />
                  <XAxis tickFormat={formatDateLabel} />
                  <YAxis tickFormat={yTickFormat} />
                  <LineSeries
                    dataKey="returns"
                    data={timeSeries}
                    variant="solid"
                    color={token('colors.dataviz.blue.2')}
                    xAccessor={d => new Date(d.date)}
                    yAccessor={d => d.cumulativeReturn}
                  />
                  <Cursor formatDataPointLabel={formatReturnsDataPointLabel} />
                </XYChart>
              </ChartProvider>
            </>
          ) : (
            <PlaceholderAreaChart
              width={width}
              height={350}
              variant={!timeSeries ? 'loading' : 'empty'}
              aria-describedby={describeId}
              aria-label={!timeSeries ? 'Loading chart' : 'No data available'}
            />
          )
        }
      </ParentSize>
    </Box>
  );
};
